/**
 * FTR4: world model. Loads the manifest and computes Float64 world positions
 * for every body from Keplerian propagation, parent-relative:
 *   moons are propagated about their planet; planets about Sol (origin).
 *
 * No Three.js here — this is the authoritative Float64 layer the renderer
 * reads and converts to Float32 camera-relative deltas (floating origin).
 */
import { propagate, vec3, add, type Vec3, type BodiesManifest, type BodyManifestEntry } from '@blackmarble/sim-core';

export interface WorldBody {
  entry: BodyManifestEntry;
  /** Float64 world position at the last update (sim units = km). */
  worldPos: Vec3;
}

export class World {
  readonly bodies: WorldBody[];
  private readonly byName: Map<string, WorldBody>;
  private readonly gmByName: Map<string, number>;

  constructor(manifest: BodiesManifest) {
    this.bodies = manifest.bodies.map((entry) => ({ entry, worldPos: vec3() }));
    this.byName = new Map(this.bodies.map((b) => [b.entry.name, b]));
    this.gmByName = new Map(manifest.bodies.map((e) => [e.name, e.gm]));
  }

  get(name: string): WorldBody | undefined {
    return this.byName.get(name);
  }

  /** Resolve the GM of a body's parent (for mean-motion when period is absent). */
  private parentGm(entry: BodyManifestEntry): number {
    if (!entry.parent) return 0;
    return this.gmByName.get(entry.parent) ?? 0;
  }

  /** Recompute every body's Float64 world position at sim time `t` (seconds). */
  update(t: number): void {
    // Parents (Sol, planets) resolve before children because the manifest is
    // ordered Sol -> planets -> their moons. We compute relative offset then
    // add the parent's already-computed world position.
    for (const b of this.bodies) {
      const { entry } = b;
      if (!entry.orbit || !entry.parent) {
        b.worldPos = vec3(); // Sol at origin
        continue;
      }
      const rel = propagate(entry.orbit, this.parentGm(entry), t);
      const parent = this.byName.get(entry.parent);
      b.worldPos = parent ? add(parent.worldPos, rel) : rel;
    }
  }

  /**
   * FLIGHT-2: nearest body's SURFACE clearance from `pos` (km) and its radius.
   * Used by supercruise to bleed speed as you approach a planet. Excludes Sol
   * so cruising past the star toward an inner planet still works.
   */
  nearestSurface(
    pos: Vec3,
    includeStar = false,
  ): { clearanceKm: number; radiusKm: number; name: string; worldPos: Vec3 } | null {
    let best: { clearanceKm: number; radiusKm: number; name: string; worldPos: Vec3 } | null = null;
    for (const b of this.bodies) {
      if (!includeStar && b.entry.name === 'Sol') continue;
      const dx = b.worldPos.x - pos.x;
      const dy = b.worldPos.y - pos.y;
      const dz = b.worldPos.z - pos.z;
      const center = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const clearance = center - b.entry.radiusKm;
      if (!best || clearance < best.clearanceKm) {
        best = {
          clearanceKm: clearance,
          radiusKm: b.entry.radiusKm,
          name: b.entry.name,
          worldPos: b.worldPos,
        };
      }
    }
    return best;
  }

  /**
   * COLLISION-2: nearest body by CENTER distance from `pos`, excluding Sol.
   *
   * Used for normal-flight collision only. Selecting by surface clearance
   * (nearestSurface) can return Sol — whose 696,000 km radius creates a
   * 703,560 km floor that blocks the ship far from any visible object when
   * includeStar=true. By picking the nearest body by center distance the
   * collision floor applies to the body the ship is actually approaching.
   */
  nearestByCenter(
    pos: Vec3,
  ): { clearanceKm: number; radiusKm: number; name: string; worldPos: Vec3 } | null {
    let best: { clearanceKm: number; radiusKm: number; name: string; worldPos: Vec3 } | null = null;
    let bestCenter = Infinity;
    for (const b of this.bodies) {
      if (b.entry.name === 'Sol') continue; // Sol excluded: floor at 703k km is too aggressive
      const dx = b.worldPos.x - pos.x;
      const dy = b.worldPos.y - pos.y;
      const dz = b.worldPos.z - pos.z;
      const center = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (center < bestCenter) {
        bestCenter = center;
        best = {
          clearanceKm: center - b.entry.radiusKm,
          radiusKm: b.entry.radiusKm,
          name: b.entry.name,
          worldPos: b.worldPos,
        };
      }
    }
    return best;
  }
}

/** Fetch + parse the bodies manifest served from /assets/bodies/. */
export const loadManifest = async (
  url = '/assets/bodies/bodies.manifest.json',
): Promise<BodiesManifest> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`manifest load failed: ${res.status}`);
  return (await res.json()) as BodiesManifest;
};
